'use client';

import React, { ChangeEvent } from 'react';
import { useFormContext } from '@/components/forms/FormContext';
import { Step } from '@/components/forms/Step';
import { SpaceWow } from '@/components/SpaceWow';
import { WizardBoxCheckbox } from '@/components/WizardBox';
import {
	IconMoneyCash,
	IconHomeDollar,
	IconMoneyHand,
	IconCaution,
	IconCheckEmblem,
	IconLeaf,
	IconInfoBubble,
} from '@/components/icons';
import { ReplaceVirtuallyFormState, StepMap, steps } from '../../constants';
import { BuyingDecisionStepActions, isValid } from './state';
import { handleNext } from './routing';
import classNames from 'classnames';
import { HeadingText } from '@/components/atoms/HeadingText';
import { ReplaceVirtuallyFormActionMap } from '../../reducer';
import { useValidateStep } from '@/components/forms/useValidateStep';
import { TextAlert } from '@/components/calculators/TextAlert';

export interface BuyingDecisionStepProps {
	testId?: string;
}

const iconStyles = 'h-auto w-full max-w-8 lg:max-w-10 fill-black';

const BuyingDecisionStep = (props: BuyingDecisionStepProps) => {
	const { testId = 'BuyingDecisionStep' } = props;

	const { state, dispatch } = useFormContext<
		StepMap,
		ReplaceVirtuallyFormState,
		ReplaceVirtuallyFormActionMap
	>();

	useValidateStep<
		StepMap,
		ReplaceVirtuallyFormState,
		ReplaceVirtuallyFormActionMap
	>(isValid, steps.BuyingDecision);

	const handleCriteriaSelectionChange = (e: ChangeEvent<HTMLInputElement>) => {
		const { value, checked } = e.target;
		if (checked) {
			dispatch({
				type: BuyingDecisionStepActions.ADD_BUYING_CRITERIA,
				payload: value,
			});
		} else {
			dispatch({
				type: BuyingDecisionStepActions.REMOVE_BUYING_CRITERIA,
				payload: value,
			});
		}
	};

	return (
		<div data-testid={testId}>
			<Step<StepMap, ReplaceVirtuallyFormState, ReplaceVirtuallyFormActionMap>
				name={steps.BuyingDecision}
				onNext={handleNext}
			>
				<HeadingText
					tag="h1"
					size="h2"
					className="mb-3 mt-10 lg:mb-4 lg:mt-20"
					balanced
				>
					What is most important to you in your buying decision?
				</HeadingText>
				<SpaceWow spacer_10 lg__spacer_14 />
				<div className="mx-auto flex max-w-5xl flex-wrap place-content-center gap-4 lg:gap-6">
					<WizardBoxCheckbox
						id="lowest-cost"
						name="lowest-cost"
						value="Lowest cost"
						label="Lowest cost"
						description="Most cost effective option"
						icon={<IconMoneyCash className={iconStyles} />}
						onChange={handleCriteriaSelectionChange}
						checked={state.buyingCriteria.includes('Lowest cost')}
						className="w-full lg:max-w-70"
					/>
					<WizardBoxCheckbox
						id="efficiency"
						name="efficiency"
						value="Efficiency"
						label="Efficiency"
						description="Lower energy bills"
						icon={
							<IconHomeDollar
								className={classNames(iconStyles, 'fill-black')}
							/>
						}
						onChange={handleCriteriaSelectionChange}
						checked={state.buyingCriteria.includes('Efficiency')}
						className="w-full lg:max-w-70"
					/>
					<WizardBoxCheckbox
						id="financing"
						name="financing"
						value="Financing"
						label="Financing"
						description="Monthly payment options"
						icon={
							<IconMoneyHand className={classNames(iconStyles, 'fill-black')} />
						}
						onChange={handleCriteriaSelectionChange}
						checked={state.buyingCriteria.includes('Financing')}
						className="w-full lg:max-w-70"
					/>
					<WizardBoxCheckbox
						id="speed-of-service"
						name="speed-of-service"
						// NOTE: This value should be "First available" unless you know you should change it. The label and description are different to improve homeowner understanding.
						// But in Hubpot, the historical/current value is "First available"
						value="First available"
						label="Speed of service"
						description="Get it done ASAP"
						icon={<IconCaution className={iconStyles} />}
						onChange={handleCriteriaSelectionChange}
						checked={state.buyingCriteria.includes('First available')}
						className="w-full lg:max-w-70"
					/>
					<WizardBoxCheckbox
						id="reliability-durability"
						name="reliability-durability"
						value="Reliability & durability"
						label="Reliability & durability"
						description="A system that will last"
						icon={
							<IconCheckEmblem
								className={classNames(iconStyles, 'fill-black')}
							/>
						}
						onChange={handleCriteriaSelectionChange}
						checked={state.buyingCriteria.includes('Reliability & durability')}
						className="w-full lg:max-w-70"
					/>
					<WizardBoxCheckbox
						id="eco-friendly"
						name="eco-friendly"
						value="Eco-friendly"
						label="Eco-friendly"
						description="Better for the environment"
						icon={<IconLeaf className={iconStyles} />}
						onChange={handleCriteriaSelectionChange}
						checked={state.buyingCriteria.includes('Eco-friendly')}
						className="w-full lg:max-w-70"
					/>
				</div>
				<SpaceWow spacer_08 lg__spacer_10 />
				<TextAlert
					renderIcon={IconInfoBubble}
					text="This helps us ensure you're getting the right system and contractor for your needs."
					title="Why are we asking?"
				/>
			</Step>
		</div>
	);
};

export default BuyingDecisionStep;
